// Small pure helpers for NotebookPage (recent pages, save status, image paste/drop targets).

export const RECENT_PAGES_STORED = 6;
export const RECENT_PAGES_SHOWN = 5;

/** Moves `entry` to the front of the recent list (deduped by page), keeping `max` entries. */
export function pushRecentPage(prev, entry, max = RECENT_PAGES_STORED) {
  const list = Array.isArray(prev) ? prev : [];
  const rest = list.filter((r) => !(r && r.sectionId === entry.sectionId && r.pageId === entry.pageId));
  return [entry, ...rest].slice(0, max);
}

/**
 * Resolves stored recent entries against the live notebook so renamed pages show
 * their current name, moved pages their current section, and deleted pages drop out.
 * The page that is already open is skipped.
 */
export function resolveRecentPages(recent, sections, currentPageId, limit = RECENT_PAGES_SHOWN) {
  if (!Array.isArray(recent) || !Array.isArray(sections)) return [];
  const byPageId = new Map();
  sections.forEach((section) => {
    (section.pages || []).forEach((page) => {
      if (!byPageId.has(String(page.id))) byPageId.set(String(page.id), { section, page });
    });
  });
  const seen = new Set();
  const out = [];
  recent.forEach((entry) => {
    if (!entry || out.length >= limit) return;
    const key = String(entry.pageId);
    if (seen.has(key) || key === String(currentPageId ?? '')) return;
    const hit = byPageId.get(key);
    if (!hit) return;
    seen.add(key);
    out.push({
      sectionId: hit.section.id,
      pageId: hit.page.id,
      pageName: hit.page.name || 'Untitled',
      sectionName: hit.section.name || '',
      color: hit.page.color || hit.section.color || null,
    });
  });
  return out;
}

/**
 * Short save state for the sidebar indicator.
 * tone: 'ok' | 'pending' | 'offline' | 'error'
 */
export function getSaveIndicator(syncStatus, manualSaveInFlight = false) {
  if (manualSaveInFlight || syncStatus === 'saving' || syncStatus === 'queued') {
    return { label: 'Saving…', tone: 'pending' };
  }
  if (syncStatus === 'offline') return { label: 'Offline', tone: 'offline' };
  if (syncStatus === 'error' || syncStatus === 'conflict' || syncStatus === 'doc_too_large') {
    return { label: 'Error', tone: 'error' };
  }
  return { label: 'Saved', tone: 'ok' };
}

function isImageFile(file) {
  return Boolean(file && typeof file.type === 'string' && file.type.startsWith('image/'));
}

/** Image files carried by a paste or drop DataTransfer. */
export function getImageFiles(dataTransfer) {
  if (!dataTransfer) return [];
  const files = Array.from(dataTransfer.files || []).filter(isImageFile);
  if (files.length > 0) return files;
  // Some browsers only expose pasted screenshots through `items`.
  return Array.from(dataTransfer.items || [])
    .filter((item) => item && item.kind === 'file' && typeof item.type === 'string' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter(Boolean);
}

/**
 * Images to attach for a paste. Clipboards that also carry plain text (e.g. cells
 * copied from a spreadsheet) paste as text, so normal text paste is untouched.
 */
export function getPastedImageFiles(clipboardData) {
  if (!clipboardData) return [];
  const text = typeof clipboardData.getData === 'function' ? clipboardData.getData('text/plain') : '';
  if (text && text.trim()) return [];
  return getImageFiles(clipboardData);
}

/** True while the user drags files (not text or sidebar rows) over the page. */
export function isFileDrag(dataTransfer) {
  const types = Array.from(dataTransfer?.types || []);
  return types.includes('Files');
}

function trimRange(value, start, end) {
  let s = start;
  let e = end;
  while (s < e && /\s/.test(value[s])) s += 1;
  while (e > s && /\s/.test(value[e - 1])) e -= 1;
  return [s, e];
}

function lineBounds(value, index) {
  const start = index === 0 ? 0 : value.lastIndexOf('\n', index - 1) + 1;
  const newline = value.indexOf('\n', index);
  return [start, newline === -1 ? value.length : newline];
}

/**
 * Text range a pasted/dropped sticky image attaches to.
 * - A non-empty selection wins (same as the selection toolbar's image button).
 * - Otherwise the line holding the caret (trimmed). A blank line uses the nearest
 *   non-blank line above it, then below it.
 * - An empty page gets a page-level note (textStart = textEnd = 0, no text).
 * Indexes are textarea offsets, like the toolbar flow.
 */
export function getStickyTargetRange(value, selectionStart, selectionEnd) {
  const text = String(value || '');
  const len = text.length;
  const clamp = (n) => Math.max(0, Math.min(len, Number.isFinite(n) ? n : 0));
  let start = clamp(selectionStart);
  let end = clamp(selectionEnd ?? selectionStart);
  if (end < start) [start, end] = [end, start];

  if (end > start) {
    const [s, e] = trimRange(text, start, end);
    if (e > s) return { textStart: s, textEnd: e };
  }

  const tryLine = (index) => {
    const [ls, le] = lineBounds(text, index);
    const [s, e] = trimRange(text, ls, le);
    return e > s ? { textStart: s, textEnd: e } : null;
  };

  const own = tryLine(start);
  if (own) return own;

  // Walk up, then down, to the nearest line with text.
  let [lineStart, lineEnd] = lineBounds(text, start);
  while (lineStart > 0) {
    const prev = tryLine(lineStart - 1);
    if (prev) return prev;
    [lineStart] = lineBounds(text, lineStart - 1);
  }
  while (lineEnd < len) {
    const next = tryLine(lineEnd + 1);
    if (next) return next;
    [, lineEnd] = lineBounds(text, lineEnd + 1);
  }
  return { textStart: 0, textEnd: 0 };
}

/** Pinned page ids, newest first, without duplicates. */
export function togglePinnedPage(pinned, pageId) {
  const list = Array.isArray(pinned) ? pinned.map(String) : [];
  const id = String(pageId);
  return list.includes(id) ? list.filter((p) => p !== id) : [id, ...list];
}

/** Live { sectionId, pageId, pageName, sectionName, color } rows for pinned ids (deleted pages drop out). */
export function resolvePinnedPages(pinned, sections) {
  const entries = (Array.isArray(pinned) ? pinned : []).map((pageId) => ({ pageId }));
  return resolveRecentPages(entries, sections, null, Number.POSITIVE_INFINITY);
}

/**
 * Moves a page within or between sections. `toIndex` is the insertion index in the
 * target section's current page list (before the page is removed). Returns the same
 * array when nothing changes, so the save effect only runs for real moves.
 */
export function movePageInSections(sections, pageId, toSectionId, toIndex) {
  if (!Array.isArray(sections)) return sections;
  const id = String(pageId);
  const from = sections.find((s) => (s.pages || []).some((p) => String(p.id) === id));
  const target = sections.find((s) => s.id === toSectionId);
  if (!from || !target || target.isFolder) return sections;
  const fromIndex = from.pages.findIndex((p) => String(p.id) === id);
  const page = from.pages[fromIndex];
  const targetLength = (target.pages || []).length;
  let index = Math.max(0, Math.min(targetLength, Number.isFinite(toIndex) ? toIndex : targetLength));

  if (from.id === target.id) {
    if (index > fromIndex) index -= 1;
    if (index === fromIndex) return sections;
    const pages = [...from.pages];
    pages.splice(fromIndex, 1);
    pages.splice(index, 0, page);
    return sections.map((s) => (s.id === from.id ? { ...s, pages } : s));
  }

  return sections.map((s) => {
    if (s.id === from.id) return { ...s, pages: s.pages.filter((p) => String(p.id) !== id) };
    if (s.id === target.id) {
      const pages = [...(s.pages || [])];
      pages.splice(index, 0, page);
      return { ...s, pages };
    }
    return s;
  });
}

/**
 * Pages whose content links to `pageId` with [[...]]. `resolveLink(linkText, sections)`
 * is findPageByName (case-insensitive, section-qualified names, aliases).
 */
export function findBacklinks(sections, pageId, parseLinks, resolveLink) {
  if (!Array.isArray(sections) || pageId == null) return [];
  const id = String(pageId);
  const out = [];
  sections.forEach((section) => {
    (section.pages || []).forEach((page) => {
      if (String(page.id) === id || !page.content || !page.content.includes('[[')) return;
      const linksHere = parseLinks(page.content).some((link) => {
        const ref = resolveLink(link.linkText, sections);
        return ref && String(ref.pageId) === id;
      });
      if (linksHere) {
        out.push({ sectionId: section.id, pageId: page.id, pageName: page.name || 'Untitled', sectionName: section.name || '' });
      }
    });
  });
  return out;
}
